import { List, ListItemButton, ListItemText } from "@mui/material";
import React, { useRef } from "react";
import { useIntl } from "react-intl";

import { SelectableItem } from "../../types/SelectableItem";
import { NestedInput } from "../shared/NestedInput/NestedInput";
import { styles } from "./RadioButtons.style";

export interface RadioButtonsProps {
  items: SelectableItem[];
  selected?: string | number;
  onSelectedChange: (selected: string | number | undefined) => void;
  customValue?: string;
  onCustomValueChange?: (customValue: string) => void;
}

export const RadioButtons: React.FC<RadioButtonsProps> = ({
  items,
  selected,
  onSelectedChange,
  customValue = null,
  onCustomValueChange,
}) => {
  const { formatMessage } = useIntl();
  const inputResetRef = useRef(() => {});

  const resetInputState = () => {
    inputResetRef.current();
  };

  const handleRadioSelect = (item: SelectableItem) => {
    item.isCustom && resetInputState();

    const customInputRadio = items.find(({ isCustom }) => isCustom);
    if (customInputRadio && customValue && customInputRadio.key !== item.key) {
      onCustomValueChange && onCustomValueChange("");
    }

    onSelectedChange(item.key);
  };

  return (
    <List
      role="radiogroup"
      component="div"
    >
      {items.map((item) => {
        const isSelected = selected === item.key;
        return (
          <ListItemButton
            role="radio"
            aria-checked={isSelected}
            key={item.key}
            selected={isSelected}
            onClick={() => handleRadioSelect(item)}
            sx={styles}
          >
            <div>
              <ListItemText
                primary={item.value}
                secondary={item.hint}
              />
              <NestedInput
                customStrings={{
                  label:
                    item.customDataLabel ??
                    formatMessage({ id: "radioButtons.customInput.label" }),
                  requiredError: formatMessage({
                    id: "radioButtons.customInput.errors.required",
                  }),
                }}
                resetInputState={(callback: () => void) =>
                  (inputResetRef.current = callback)
                }
                item={item}
                isSelected={isSelected}
                customValue={customValue}
                onCustomValueChange={onCustomValueChange}
                onSelectedChange={() => onSelectedChange(item.key)}
              />
            </div>
          </ListItemButton>
        );
      })}
    </List>
  );
};
